import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { inventoryItemsAPI, inventoryLotsAPI, productsAPI, InventoryItem, InventoryItemInput, InventoryLot, Product } from '../../services/api';
import InventoryLocationPicker from './InventoryLocationPicker';

interface InventoryItemFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  item?: InventoryItem | null;
}

const InventoryItemForm: React.FC<InventoryItemFormProps> = ({ isOpen, onClose, onSuccess, item }) => {
  const [formData, setFormData] = useState({
    productId: '',
    trayId: '',
    lotId: '',
    quantity: '',
    unit: '',
    unitCost: '',
    costCurrency: 'USD',
    status: 'IN_STOCK',
    batchNumber: '',
    expiryDate: '',
    notes: '',
  });
  const [products, setProducts] = useState<Product[]>([]);
  const [lots, setLots] = useState<InventoryLot[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchData();
      if (item) {
        setFormData({
          productId: item.productId.toString(),
          trayId: item.trayId?.toString() || '',
          lotId: item.lotId?.toString() || '',
          quantity: item.quantity.toString(),
          unit: item.unit || item.product?.unit || '',
          unitCost: item.unitCost?.toString() || '',
          costCurrency: item.costCurrency || 'USD',
          status: item.status,
          batchNumber: item.lotId ? '' : item.batchNumber || '',
          expiryDate: item.expiryDate ? item.expiryDate.split('T')[0] : '',
          notes: item.notes || '',
        });
      } else {
        setFormData({
          productId: '',
          trayId: '',
          lotId: '',
          quantity: '',
          unit: '',
          unitCost: '',
          costCurrency: 'USD',
          status: 'IN_STOCK',
          batchNumber: '',
          expiryDate: '',
          notes: '',
        });
      }
      setError('');
    }
  }, [isOpen, item]);

  const fetchData = async () => {
    try {
      const [productsData, lotsData] = await Promise.all([
        productsAPI.getAll(),
        inventoryLotsAPI.getAll({ isActive: true }),
      ]);
      setProducts(productsData);
      setLots(lotsData);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const quantity = Number(formData.quantity);
      if (!formData.productId || !Number.isInteger(quantity) || quantity < 1) {
        setError('Product and a quantity greater than zero are required');
        return;
      }

      const payload: InventoryItemInput = {
        productId: parseInt(formData.productId),
        trayId: formData.trayId ? parseInt(formData.trayId) : null,
        quantity,
        unit: formData.unit.trim() || undefined,
        unitCost: formData.unitCost ? Number(formData.unitCost) : undefined,
        costCurrency: formData.unitCost ? formData.costCurrency.trim().toUpperCase() || 'USD' : undefined,
        status: formData.status as InventoryItem['status'],
        lotId: formData.lotId ? parseInt(formData.lotId) : null,
        batchNumber: formData.lotId ? null : formData.batchNumber.trim() || null,
        expiryDate: formData.expiryDate ? new Date(formData.expiryDate).toISOString() : null,
        notes: formData.notes.trim() || null,
      };

      if (item) {
        await inventoryItemsAPI.update(item.id, payload);
      } else {
        await inventoryItemsAPI.create(payload);
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save inventory item');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-neutral-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b">
          <h3 className="text-lg font-medium text-neutral-900">
            {item ? 'Edit Inventory Item' : 'Add Inventory Item'}
          </h3>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-500">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-error-50 border border-error-200 text-error-600 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700">Product *</label>
              <select
                required
                value={formData.productId}
                onChange={(e) => {
                  const selectedProduct = products.find((product) => product.id === Number(e.target.value));
                  setFormData({
                    ...formData,
                    productId: e.target.value,
                    unit: selectedProduct?.unit || formData.unit,
                  });
                }}
                className="mt-1 block w-full px-3 py-2.5 rounded-lg border border-neutral-300 bg-white shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50 sm:text-sm transition-colors"
              >
                <option value="">Select Product</option>
                {products.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name} ({product.sku})
                  </option>
                ))}
              </select>
            </div>

            <div className="col-span-2">
              <InventoryLocationPicker
                trayId={formData.trayId}
                onTrayIdChange={(trayId) => setFormData({ ...formData, trayId })}
                initialTray={item?.tray || null}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="inventory-quantity" className="block text-sm font-medium text-neutral-700">Quantity *</label>
              <input
                id="inventory-quantity"
                type="number"
                required
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                className="mt-1 block w-full px-3 py-2.5 rounded-lg border border-neutral-300 bg-white shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50 sm:text-sm transition-colors"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700">Unit</label>
              <input
                type="text"
                value={formData.unit}
                onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                placeholder="e.g. kg, liter, pcs"
                className="mt-1 block w-full px-3 py-2.5 rounded-lg border border-neutral-300 bg-white shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50 sm:text-sm transition-colors"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700">Status *</label>
              <select
                required
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="mt-1 block w-full px-3 py-2.5 rounded-lg border border-neutral-300 bg-white shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50 sm:text-sm transition-colors"
              >
                <option value="IN_STOCK">In Stock</option>
                <option value="LOW_STOCK">Low Stock</option>
                <option value="OUT_OF_STOCK">Out of Stock</option>
                <option value="RESERVED">Reserved</option>
                <option value="DAMAGED">Damaged</option>
              </select>
            </div>
            <div>
              <label htmlFor="inventory-unit-cost" className="block text-sm font-medium text-neutral-700">Unit Cost</label>
              <input
                id="inventory-unit-cost"
                type="number"
                min="0"
                step="0.0001"
                value={formData.unitCost}
                onChange={(e) => setFormData({ ...formData, unitCost: e.target.value })}
                placeholder="Receipt-time cost"
                className="mt-1 block w-full px-3 py-2.5 rounded-lg border border-neutral-300 bg-white shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50 sm:text-sm transition-colors"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700">Cost Currency</label>
              <input
                type="text"
                maxLength={3}
                value={formData.costCurrency}
                onChange={(e) => setFormData({ ...formData, costCurrency: e.target.value.toUpperCase() })}
                disabled={!formData.unitCost}
                placeholder="USD"
                className="mt-1 block w-full px-3 py-2.5 rounded-lg border border-neutral-300 bg-white shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50 sm:text-sm transition-colors disabled:bg-neutral-100"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700">Lot</label>
              <select
                value={formData.lotId}
                onChange={(e) => setFormData({ ...formData, lotId: e.target.value, batchNumber: e.target.value ? '' : formData.batchNumber })}
                className="mt-1 block w-full px-3 py-2.5 rounded-lg border border-neutral-300 bg-white shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50 sm:text-sm transition-colors"
              >
                <option value="">Create/find by lot number below</option>
                {lots.map((lot) => (
                  <option key={lot.id} value={lot.id}>
                    {lot.lotNumber}{lot.supplier?.name ? ` - ${lot.supplier.name}` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700">New Lot Number</label>
              <input
                type="text"
                value={formData.batchNumber}
                onChange={(e) => setFormData({ ...formData, batchNumber: e.target.value })}
                disabled={Boolean(formData.lotId)}
                placeholder={formData.lotId ? 'Using selected lot' : 'Type a lot number'}
                className="mt-1 block w-full px-3 py-2.5 rounded-lg border border-neutral-300 bg-white shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50 sm:text-sm transition-colors"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700">Expiry Date</label>
              <input
                type="date"
                value={formData.expiryDate}
                onChange={(e) => setFormData({ ...formData, expiryDate: e.target.value })}
                className="mt-1 block w-full px-3 py-2.5 rounded-lg border border-neutral-300 bg-white shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50 sm:text-sm transition-colors"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="mt-1 block w-full px-3 py-2.5 rounded-lg border border-neutral-300 bg-white shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50 sm:text-sm transition-colors"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-neutral-300 rounded-md text-sm font-medium text-neutral-700 hover:bg-neutral-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : item ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InventoryItemForm;
